/* ============================================================
   PLATONIC — Product (PDP) behaviours
   Ported from the prototype's inline PDP script. The cart engine is
   replaced with real Shopify /cart/add.js (mirrors platonic-home.js):
   ADD TO BAG / sticky → /cart, BUY NOW → /checkout, "+ Pair" → /cart.
   ============================================================ */
(function () {
  'use strict';

  const MAX_QTY = 10; // per-line cap — matches the cart drawer + checkout validation function

  /* ---- Toast (shared by add-to-cart + share) ---- */
  function showToast(msg) {
    let t = document.querySelector('.pl-toast');
    if (!t) { t = document.createElement('div'); t.className = 'pl-toast'; document.body.appendChild(t); }
    t.textContent = msg;
    t.classList.add('is-on');
    clearTimeout(t._timer);
    t._timer = setTimeout(() => t.classList.remove('is-on'), 1800);
  }

  /* ---- Gallery: thumbnail rail + prev/next + click-on-stage ---- */
  function initGallery() {
    const stage = document.querySelector('[data-gallery-image]');
    if (!stage) return;
    const imgs = Array.from(document.querySelectorAll('[data-gallery-images] [data-img]')).map((n) => ({ src: n.dataset.src, srcset: n.dataset.srcset || '', alt: n.dataset.alt || '' }));
    const total = imgs.length;
    if (!total) return;
    const thumbs = Array.from(document.querySelectorAll('[data-gallery-rail] [data-thumb]'));
    let i = 0;
    const counterEl = document.querySelector('[data-gallery-current]');
    const totalEl = document.querySelector('[data-gallery-total]');
    if (totalEl) totalEl.textContent = String(total);
    const update = () => {
      stage.srcset = imgs[i].srcset;
      stage.src = imgs[i].src;
      stage.alt = imgs[i].alt || '';
      if (counterEl) counterEl.textContent = String(i + 1);
      thumbs.forEach((t, idx) => {
        const active = idx === i;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };
    thumbs.forEach((t, idx) => t.addEventListener('click', () => { i = idx; update(); }));
    const prev = document.querySelector('[data-gallery-prev]');
    const next = document.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', () => { i = (i - 1 + total) % total; update(); });
    if (next) next.addEventListener('click', () => { i = (i + 1) % total; update(); });
    stage.addEventListener('click', () => { i = (i + 1) % total; update(); });

    // Gallery zoom REMOVED 2026-08-16 on the founder's call — both the desktop
    // hover zoom and the mobile pinch zoom. Do not re-add without an explicit ask.

    // The gallery images live as data-src strings (never in the DOM as <img>), so a
    // thumbnail click used to trigger a fresh ~1000w download — a visible 1-2s stall.
    // Warm them into the browser cache during idle time (after the LCP image, so this
    // never competes with initial load). Matches the stage img's own sizes/srcset so the
    // preloaded resource is the exact one the swap resolves to → switches become instant.
    stage.decoding = 'async';
    const sizesAttr = stage.getAttribute('sizes') || '';
    const preloadAll = () => {
      imgs.forEach((im) => {
        if (!im.src) return;
        const pre = new Image();
        if (sizesAttr) pre.sizes = sizesAttr;
        if (im.srcset) pre.srcset = im.srcset;
        pre.src = im.src;
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(preloadAll, { timeout: 3000 });
    else setTimeout(preloadAll, 1200);
  }

  /* ---- Quantity stepper: drives price row + every CTA price ---- */
  function initQty() {
    const v = document.querySelector('.pl-qty [data-qty-value]');
    const minus = document.querySelector('.pl-qty [data-qty-minus]');
    const plus = document.querySelector('.pl-qty [data-qty-plus]');
    const row = document.querySelector('.pl-price-row');
    const priceEl = document.querySelector('.pl-price');
    const ctaPrices = document.querySelectorAll('[data-cta-price]');
    const unit = row ? parseInt(row.getAttribute('data-unit'), 10) : 0;
    const fmt = (base, n) => '₹' + (base * n).toLocaleString('en-IN');
    const sync = () => {
      const n = parseInt((v && v.textContent) || '1', 10);
      if (unit) {
        ctaPrices.forEach((el) => { el.textContent = fmt(unit, n); });
        if (priceEl) priceEl.textContent = fmt(unit, n);
      }
    };
    if (!v || !minus || !plus) return;
    minus.addEventListener('click', () => { v.textContent = String(Math.max(1, parseInt(v.textContent || '1', 10) - 1)); sync(); });
    plus.addEventListener('click', () => { v.textContent = String(Math.min(MAX_QTY, parseInt(v.textContent || '1', 10) + 1)); sync(); });
    // Only re-sync (reformat with grouping) once qty changes; leave the
    // server-rendered money string in place at load so it matches store format.
  }

  function currentQty() {
    const v = document.querySelector('.pl-qty [data-qty-value]');
    return Math.min(MAX_QTY, Math.max(1, parseInt((v && v.textContent) || '1', 10)));
  }

  /* ---- Sticky ATC: reveal once the buy row leaves the top ---- */
  function initSticky() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const buyRow = document.querySelector('.pl-buy-row') || document.querySelector('.pl-price-row');
    if (!sticky || !buyRow) return;
    const toTop = document.querySelector('[data-to-top]');
    const footer = document.querySelector('.footer.pl-footer');
    const onScroll = () => {
      // Show once the buy box has scrolled past — but retire the bar as soon as
      // the footer enters the viewport, so it never docks over the sign-off
      // reveal (founder screenshot 2026-08-16: the bar sat on the wordmark
      // plate and killed the moment).
      const past = buyRow.getBoundingClientRect().bottom < 0
        && !(footer && footer.getBoundingClientRect().top < window.innerHeight);
      sticky.classList.toggle('is-visible', past);
      sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
      if (past) sticky.removeAttribute('inert'); else sticky.setAttribute('inert', '');
      // On mobile the bar docks to the bottom — lift the back-to-top FAB above it.
      if (toTop) toTop.classList.toggle('atc-docked', past);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* ---- Notes stage cards: tap to toggle (CSS handles hover) ---- */
  function initStages() {
    const stages = document.querySelectorAll('[data-stage]');
    if (!stages.length) return;
    const toggle = (s) => {
      const open = s.classList.contains('is-open');
      stages.forEach((o) => { o.classList.remove('is-open'); o.setAttribute('aria-expanded', 'false'); });
      if (!open) { s.classList.add('is-open'); s.setAttribute('aria-expanded', 'true'); }
    };
    stages.forEach((s) => {
      s.addEventListener('click', () => toggle(s));
      s.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(s); }
      });
    });
  }

  /* ---- Add-to-cart via real Shopify /cart/add.js ---- */
  function initATC() {
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    document.querySelectorAll('[data-atc]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const id = btn.getAttribute('data-variant-id');
        const fallback = btn.getAttribute('data-href');
        if (!id) { if (fallback) window.location.href = fallback; return; }
        // Buttons inside the main buy stack honour the quantity stepper;
        // the "+ Pair" buttons in the Six strip always add a single unit.
        const qty = btn.hasAttribute('data-qty-source') ? currentQty() : 1;
        const dest = btn.hasAttribute('data-buy-now') ? '/checkout' : cartUrl;
        btn.setAttribute('aria-busy', 'true');
        try {
          // BUY NOW: unchanged — raw add then hand straight to checkout (Razorpay Magic
          // Checkout hooks .pl-cta-buy). The server-side qty Function backstops the cap.
          if (btn.hasAttribute('data-buy-now')) {
            const res = await fetch(addUrl + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }] }),
            });
            if (!res.ok) throw new Error('add failed');
            window.location.href = dest; // → /checkout
            return;
          }
          // ADD TO BAG / sticky / "+ Pair": route through the drawer's addVariant, which
          // sets the line to an ABSOLUTE quantity clamped at MAX_QTY — so an existing line
          // can never be pushed past the 10/line cap (a raw additive /cart/add.js could).
          // refresh() first so the cap is computed against the true current cart.
          if (window.PlatonicCart && window.PlatonicCart.addVariant) {
            await window.PlatonicCart.refresh();
            await window.PlatonicCart.addVariant(Number(id), qty); // addVariant opens the drawer
            btn.removeAttribute('aria-busy');
          } else {
            const res = await fetch(addUrl + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }] }),
            });
            if (!res.ok) throw new Error('add failed');
            window.location.href = dest;
          }
        } catch (err) {
          btn.removeAttribute('aria-busy');
          // The add genuinely failed (network/422 over-inventory). Don't send the
          // shopper to a fallback page that would show nothing added — tell them.
          showToast('Could not add to bag. Please try again.');
        }
      });
    });
  }

  /* ---- Share: Facebook / Instagram / WhatsApp / Copy link ---- */
  function initShare() {
    const share = document.querySelectorAll('[data-share]');
    if (!share.length) return;
    const url = window.location.href;
    const title = document.title;
    const productName = title.replace(/\s*[—-]\s*Platonic\s*$/i, '').trim();
    const text = productName + ' by Platonic';
    const copy = async () => {
      try { await navigator.clipboard.writeText(url); return true; }
      catch (_) {
        const ta = document.createElement('textarea');
        ta.value = url; ta.setAttribute('readonly', '');
        ta.style.position = 'fixed'; ta.style.left = '-9999px';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); document.body.removeChild(ta); return true; }
        catch (_) { document.body.removeChild(ta); return false; }
      }
    };
    /* Native OS share sheet. On a phone this is the ONLY route that works for
       Facebook and Instagram:
         - facebook.com/sharer is intercepted by the Facebook app, which cannot
           handle that URL and lands the user on a blank screen (reported 2026-08-01)
         - Instagram has no web share endpoint at all, so the old behaviour could
           only copy the link, which reads as broken
       The sheet hands off to the real app with the URL intact. Desktop browsers
       have no navigator.share, so those paths fall through to what they did before
       (the Facebook popup works fine on desktop). A cancelled sheet counts as
       handled — do NOT fall through, or cancelling would fire the fallback. */

    share.forEach((a) => {
      a.addEventListener('click', async (e) => {
        e.preventDefault();
        const kind = a.getAttribute('data-share');
        if (kind === 'whatsapp') {
          window.open('https://wa.me/?text=' + encodeURIComponent(text + ' ' + url), '_blank', 'noopener,noreferrer');
        } else if (kind === 'copy') {
          const ok = await copy();
          showToast(ok ? 'Link copied' : 'Could not copy link');
        }
      });
    });
  }

  /* ---- PDP film: accessible pause/play + reduced-motion (WCAG 2.2.2 Pause,
     Stop, Hide). Adds the missing "mechanism to pause" and holds on the poster
     for reduced-motion users; still autoplays for everyone else. ---- */
  function initProductVideo() {
    const band = document.querySelector('[data-video-band]');
    if (!band) return;
    const video = band.querySelector('video');
    const btn = band.querySelector('[data-video-toggle]');
    if (!video || !btn) return;

    const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const play = () => { const p = video.play(); if (p && p.catch) p.catch(() => {}); };
    const setState = (playing) => {
      band.classList.toggle('is-playing', playing);
      band.classList.toggle('is-paused', !playing);
      btn.setAttribute('aria-pressed', String(playing));
      btn.setAttribute('aria-label', playing ? 'Pause film' : 'Play film');
    };

    // Track explicit user intent so the in-view observer never overrides a manual pause.
    let userPaused = false;

    // Reduced-motion users: never autoplay — hold on the poster until they choose to play.
    if (reduce) { video.removeAttribute('autoplay'); video.pause(); userPaused = true; }
    setState(!video.paused);

    btn.addEventListener('click', () => {
      if (video.paused) { userPaused = false; play(); }
      else { userPaused = true; video.pause(); }
    });
    video.addEventListener('play', () => setState(true));
    video.addEventListener('pause', () => setState(false));

    // Deferred load: the <video> ships with preload="none" and no autoplay, so the
    // first scroll-into-view is what downloads + starts it — keeping the ~2.5MB payload
    // off the initial load / LCP path. Also pauses when scrolled away (CPU/battery),
    // and never overrides an explicit user pause.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { if (!userPaused) play(); } else { video.pause(); } });
      }, { threshold: 0.25 });
      io.observe(video);
    }
  }

  function init() {
    initGallery();
    initQty();
    initSticky();
    initStages();
    initATC();
    initShare();
    initProductVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
